import Cookies from 'js-cookie';
import { flattenQuestionsForApi } from '../components/CreateNewTask/utils/questionStructure.js';

const deleteCookieByKey = (key) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

export const setLocalStorage = (key, value) => {
  // Never persist undefined/null: JSON.stringify(undefined) === undefined, which
  // localStorage.setItem coerces to the literal string "undefined" — and the next
  // getLocalStorage then throws `"undefined" is not valid JSON` on JSON.parse,
  // crashing the auth/render path (e.g. an exchange response missing a field).
  if (value === undefined || value === null) {
    localStorage.removeItem(key);
    deleteCookieByKey(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(value));
  deleteCookieByKey(key);
};

export const getLocalStorage = (key) => {
  const localStorageValue = localStorage.getItem(key);
  // Tolerate legacy "undefined"/"null" strings and any non-JSON value instead of
  // throwing — a single malformed key must not crash the whole app.
  if (
    localStorageValue &&
    localStorageValue !== 'undefined' &&
    localStorageValue !== 'null'
  ) {
    try {
      return JSON.parse(localStorageValue);
    } catch (e) {
      return localStorageValue;
    }
  }
  return Cookies.get(key);
};

export const deleteLocalStorage = (key) => {
  localStorage.removeItem(key);
};

/**
 * Resolve user states from profile object.
 * Returns non-empty array of state codes, or null if no states are set.
 */
export const resolveProfileStates = (profile) => {
  if (Array.isArray(profile?.states) && profile.states.filter(Boolean).length > 0) {
    return profile.states.filter(Boolean);
  }
  if (typeof profile?.state === 'string' && profile.state.trim()) {
    return [profile.state.trim()];
  }
  return null;
};

export const getUserStates = () => {
  const stored = getLocalStorage('states');
  if (Array.isArray(stored) && stored.filter(Boolean).length > 0) return stored.filter(Boolean);
  const single = getLocalStorage('state');
  if (typeof single === 'string' && single.trim()) return [single.trim()];
  return [];
};

const cloneAdvancedMarkingSettingsByClassId = (settingsByClassId) => {
  if (
    !settingsByClassId ||
    typeof settingsByClassId !== 'object' ||
    Array.isArray(settingsByClassId)
  ) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(settingsByClassId).map(([classId, controls]) => [
      classId,
      controls && typeof controls === 'object' ? { ...controls } : controls,
    ]),
  );
};

export const buildDuplicateAssignmentPayload = (assignment) => {
  if (!assignment) {
    throw new Error('Task not found for duplication');
  }

  if (
    !Array.isArray(assignment.questions) ||
    !Array.isArray(assignment.classIds)
  ) {
    throw new Error('Invalid task data for duplication');
  }

  return {
    title: `Copy of ${assignment.title || 'Untitled Task'}`,
    questions: flattenQuestionsForApi(assignment.questions || []),
    classIds: assignment.classIds || [],
    dueAt: assignment.dueAt,
    reviewedBy: assignment.reviewedBy,
    reviewers: assignment.reviewers || {},
    subject: assignment.subject || '',
    subjects: assignment.subjects || [],
    years: assignment.years || [],
    taskType: assignment.taskType,
    advancedMarkingSettingsByClassId: cloneAdvancedMarkingSettingsByClassId(
      assignment.advancedMarkingSettingsByClassId,
    ),
  };
};

export const duplicateAssignment = async (id, assignments) => {
  const assignment = assignments.find(
    (assignment) => String(assignment.id) === String(id),
  );

  const copyAssignment = buildDuplicateAssignmentPayload(assignment);
  const { createAssignment } = await import('../service');
  const createdAssignment = await createAssignment(copyAssignment);
  if (!createdAssignment) {
    throw new Error('Failed to duplicate task');
  }
  return createdAssignment;
};
